import { StatCard } from "./StatCard";
import { usd, usdSigned } from "./format";
import { INVESTOR_LABEL, type Investor } from "./PrcFilterBar";
import type {
  FacilityCapacityResponse,
  PortfolioBlock,
} from "../../utils/portfolio-risk-concentration/api/facility-capacity";
import { cn } from "../../utils/common/cn";

/**
 * The Capacity page's headline cards — issue #197, built to the three tables in it.
 *
 * ── Which table shows when ──────────────────────────────────────────────────────────────
 * Keyed off the controls that already exist, because the facilities ARE the debt: Wafra
 * funds through CAP 1 and CAP 2 and gives us the external limits on them, Navitas funds
 * through equity, and we set internal limits against whichever capital backs the investor.
 * So Scope and Facility already say which view you are in, and no fourth filter value was
 * added for it.
 *
 *   External + All facilities   "All", and beneath it the COMBINED "Committed ceiling" —
 *                               the funding mix, then how drawn the facilities are together
 *   External + CAP 1 / CAP 2    "Committed ceiling" for that book alone
 *   Internal                    the Treasury-set appetite ceiling — "Equity ceiling" for an
 *                               investor who funds with equity, "Internal cap" otherwise
 *
 * The combined table rides under "All" because it has nowhere else to go: picking a single
 * book shows that book alone, and the mix above answers how the portfolio is FUNDED rather
 * than how far it is drawn. It is the issue's third table as literally written — Wafra's two
 * facilities together at 69.1% used, 30.9% unused.
 *
 * ── The "All" figures do not vary by investor, and must not ─────────────────────────────
 * One portfolio is funded by Wafra's two facilities and Navitas' equity together, so the mix
 * is a fact about the portfolio rather than about whoever's limits are on screen. An earlier
 * cut scoped the pool to the selected investor AND counted their internal cap as equity,
 * which reported a $5,200,000,000 total portfolio for Wafra — a $2.7B facility plus a $2.5B
 * appetite ceiling over the very book it funds — and claimed Wafra was 48.1% equity funded
 * when Wafra funds entirely with debt. The internal cap is appetite, not money, and is kept
 * out of the pool for that reason.
 *
 * ── Every ceiling is measured against ITS OWN book ──────────────────────────────────────
 * Never the portfolio's, unless the ceiling is the portfolio's. A single facility uses its
 * own drawn book; the combined debt table uses both facilities'; the internal table uses the
 * book of the facilities THAT INVESTOR funds.
 *
 * An earlier cut used the portfolio total everywhere, which put the same $1,866,182,976 on
 * Wafra's card and Navitas' — both claiming the whole book — and read 484.7% for Navitas,
 * whose $385M is not behind $1.87B of contracts. The portfolio figure was someone else's
 * book measured on their yardstick.
 *
 * Where no book can be named, the cards say so rather than borrowing one. An investor
 * funding only with equity owns no facility, and exposure is not attributed by investor
 * anywhere in the warehouse — `bankdwn_daily.investor_code` is a bare numeric with no lookup
 * table, and a single code holds 99.7% of the book. That is a data gap, not a display
 * choice, and it closes the day contracts carry a funding source.
 */

/** Percent of a whole, or an em dash when the whole is zero. */
function pctOf(part: number, whole: number): string {
  return whole ? `${((part / whole) * 100).toFixed(1)}%` : "—";
}

/**
 * Tailwind needs literal class names — a template string built from a count is invisible to
 * the JIT scanner and produces an unstyled grid. So the widths are enumerated.
 */
const GRID_BY_COUNT: Record<number, string> = {
  3: "grid grid-cols-2 gap-3 lg:grid-cols-3",
  4: "grid grid-cols-2 gap-3 lg:grid-cols-4",
  5: "grid grid-cols-2 gap-3 lg:grid-cols-5",
};

/**
 * "All" — the funding mix.
 *
 * TOTAL PORTFOLIO is the capital POOL (committed debt ceilings + the equity ceiling), not
 * the deployed book. That is what makes the two middle cards a mix adding to 100%: measured
 * against the book instead, debt would be 100% and equity 0%, because every contract sits
 * inside a debt facility.
 */
function AllCards({ p }: { p: PortfolioBlock }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <StatCard
        aligned
        label="Total portfolio"
        value={usd(p.totalPortfolioDollars)}
        sublabel={`Across ${p.facilityCount} ${
          p.facilityCount === 1 ? "facility" : "facilities"
        } + equity`}
      />
      <StatCard
        aligned
        label="Debt funded"
        value={p.debtFundedPct === null ? "—" : `${p.debtFundedPct.toFixed(1)}%`}
        sublabel={`of total portfolio · ${usd(p.debtCeilingDollars)} committed`}
      />
      <StatCard
        aligned
        label="Equity funded"
        value={p.equityFundedPct === null ? "—" : `${p.equityFundedPct.toFixed(1)}%`}
        sublabel={`of total portfolio · ${usd(p.equityCeilingDollars)} of equity`}
      />
      <StatCard
        aligned
        // Signed, matching the rest of the product: a facility past its commitment reports
        // the overage rather than a zero that reads as "exactly full".
        label="Remaining debt capacity"
        value={usdSigned(p.remainingDebtCapacityDollars)}
        sublabel="Sum of committed ceilings minus debt outstanding"
        accent={p.remainingDebtCapacityDollars < 0 ? "red" : "default"}
      />
      {/* The one figure with no source. Assuming debt draws first would make equity 100%
          unused; measuring the whole book against it would make it 485% used. Both are
          inventions, so the card says which question cannot be answered yet. */}
      {p.remainingEquityCapacityDollars === null ? (
        <StatCard
          aligned
          label="Remaining equity capacity"
          value={<span className="text-muted-foreground">Not attributed</span>}
          sublabel={p.equityAttributionNote}
        />
      ) : (
        <StatCard
          aligned
          label="Remaining equity capacity"
          value={usdSigned(p.remainingEquityCapacityDollars)}
          sublabel={`${pctOf(
            p.remainingEquityCapacityDollars,
            p.equityCeilingDollars,
          )} of equity ceiling unused`}
          accent={p.remainingEquityCapacityDollars < 0 ? "red" : "default"}
        />
      )}
    </div>
  );
}

/**
 * The five-card ceiling table, used for both Equity and Committed ceiling.
 *
 * One component for both because the issue calls them "structurally parallel" and they are:
 * same five cards, same order, same arithmetic. Only the ceiling's name, its caption and the
 * phrase the percentages are "of" differ. Writing them twice would let the two drift.
 */
function CeilingCards({
  label,
  ceiling,
  ceilingSublabel,
  outstanding,
  loans,
  leases,
  unattributedNote,
}: {
  /** "Equity ceiling" or "Committed ceiling". */
  label: string;
  ceiling: number;
  ceilingSublabel: string;
  /**
   * The book behind THIS ceiling, passed in rather than taken off the portfolio block.
   * A single facility is measured against its own drawn book, not the whole portfolio —
   * CAP 1's $1,749.3M against its $1.7B ceiling is 102.9%, where the portfolio total would
   * have read 109.8% and quietly charged CAP 1 for CAP 2's leases.
   */
  outstanding: number | null;
  /**
   * Loans and leases outstanding. THREE states, and the difference between the last two is
   * the whole point:
   *
   *   number     — a figure to show. **Including 0.** A book that is genuinely empty is a
   *                fact about the data and stays on screen saying so.
   *   null       — the warehouse cannot attribute it (see `unattributedNote`).
   *   undefined  — that book is not in the selection at all, so the card is not rendered.
   *
   * Only the caller knows which zero it is holding. On CAP 1 the leases figure is zero
   * because CAP 2 is not being looked at — a structural zero, and a card reading "Leases
   * outstanding 0.0% / $0" there invites the reader to conclude the leases book is empty. A
   * zero that arrives in `p.leasesOutstandingDollars` under All Facilities means something
   * completely different and must still render.
   */
  loans: number | null | undefined;
  leases: number | null | undefined;
  /** Shown on the four outstanding cards when `outstanding` is null. */
  unattributedNote?: string;
}) {
  const of = `of ${label.toLowerCase()}`;

  // No book behind this ceiling that can be named. Four cards say so rather than borrowing
  // the portfolio's figures — the whole book against one investor's ceiling is not that
  // investor's utilisation, it is someone else's book measured on their yardstick.
  if (outstanding === null || loans === null || leases === null) {
    return (
      <div className={GRID_BY_COUNT[5]}>
        <StatCard aligned label={label} value={usd(ceiling)} sublabel={ceilingSublabel} />
        {["Total outstanding", "Loans outstanding", "Leases outstanding", "Remaining capacity"].map(
          (l) => (
            <StatCard
              aligned
              key={l}
              label={l}
              value={<span className="text-muted-foreground">Not attributed</span>}
              sublabel={unattributedNote}
            />
          ),
        )}
      </div>
    );
  }

  const remaining = ceiling - outstanding;
  // Three cards always, plus whichever book the selection actually covers.
  const cardCount = 3 + (loans === undefined ? 0 : 1) + (leases === undefined ? 0 : 1);
  return (
    <div className={GRID_BY_COUNT[cardCount]}>
      <StatCard aligned label={label} value={usd(ceiling)} sublabel={ceilingSublabel} />
      {/* The PERCENTAGE leads on these four and the dollars caption them, because the
          issue's tables are written that way — "69.1% of committed ceiling" is the figure
          being compared across cards, and the absolute is the supporting detail. */}
      <StatCard
        aligned
        label="Total outstanding"
        value={pctOf(outstanding, ceiling)}
        sublabel={`${of} · ${usd(outstanding)}`}
        accent={remaining < 0 ? "red" : outstanding / (ceiling || 1) >= 0.9 ? "amber" : "default"}
      />
      {/* `!== undefined`, deliberately NOT a truthiness test. `loans && <StatCard/>` would
          drop a genuine $0 — the one figure that most needs saying — along with the
          structural one. */}
      {loans !== undefined && (
        <StatCard
          aligned
          label="Loans outstanding"
          value={pctOf(loans, ceiling)}
          sublabel={`${of} · ${usd(loans)}`}
        />
      )}
      {leases !== undefined && (
        <StatCard
          aligned
          label="Leases outstanding"
          value={pctOf(leases, ceiling)}
          sublabel={`${of} · ${usd(leases)}`}
        />
      )}
      <StatCard
        aligned
        label="Remaining capacity"
        value={pctOf(Math.abs(remaining), ceiling)}
        sublabel={`${of} ${remaining < 0 ? "over" : "unused"} · ${usdSigned(remaining)}`}
        accent={remaining < 0 ? "red" : "default"}
      />
    </div>
  );
}

export function CapacityCards({
  data,
  scope,
  facility,
  investor,
  facilityLabel,
  className,
}: {
  data: FacilityCapacityResponse;
  scope: "EXTERNAL" | "INTERNAL";
  /** The Facility selection: ALL, CAP1 or CAP2. Ignored on the internal scope. */
  facility: string;
  investor: Investor;
  /** Book name as the page spells it, e.g. "CAP 1 Back Book". */
  facilityLabel?: string;
  className?: string;
}) {
  const p = data.portfolio;
  // An older backend has no portfolio block. Rendering nothing beats rendering five cards
  // of zeroes that look like a book with no capital behind it.
  if (!p) return null;

  const investorName = INVESTOR_LABEL[investor] ?? investor;
  // Whether this investor's capital is equity, from the registry rather than a hardcoded
  // name — the day another investor funds with equity, the label follows.
  const isEquityBacked = p.equityInvestors.includes(investor);

  return (
    <div className={cn("space-y-3", className)}>
      {scope === "INTERNAL" ? (
        <CeilingCards
          // Named for what actually backs the investor. Navitas funds through equity, so
          // theirs IS the equity ceiling; Wafra funds through debt only, and labelling their
          // $2.5B appetite cap "Equity ceiling" would state something false on screen.
          label={isEquityBacked ? "Equity ceiling" : "Internal cap"}
          // The appetite ceiling, which is what every internal limit below is measured
          // against (prc_limit_config.total_capital_usd) — so the card and the table agree.
          ceiling={p.internalCapDollars ?? 0}
          ceilingSublabel={`${investorName} · set internally by Treasury`}
          // THIS investor's book — the contracts inside the facilities they fund — not the
          // portfolio's. Null for an investor who funds only with equity, because exposure
          // is not attributed by investor anywhere in the warehouse.
          outstanding={p.investorOutstandingDollars}
          loans={p.investorLoansDollars}
          leases={p.investorLeasesDollars}
          unattributedNote={p.investorAttributionNote}
        />
      ) : facility === "ALL" ? (
        <>
          {/* The combined debt view LEADS: Wafra's two facilities together, how drawn they
              are. It is the question the page is opened for — how much room is left — so it
              takes the top row, and the funding mix sits underneath as context.

              These two rows were the other way round until the drawn book became the
              headline. Nothing else moved: each row keeps its own five cards in their own
              order, only the rows swapped. */}
          <CeilingCards
            label="Committed ceiling"
            ceiling={p.debtCeilingDollars}
            ceilingSublabel={`${(p.debtInvestors.map(
              (i) => INVESTOR_LABEL[i as Investor] ?? i,
            ).join(", ") || investorName)} · ${p.facilityCount} facilities`}
            outstanding={p.totalOutstandingDollars}
            loans={p.loansOutstandingDollars}
            leases={p.leasesOutstandingDollars}
          />
          <AllCards p={p} />
        </>
      ) : (
        <CeilingCards
          label="Committed ceiling"
          // The SELECTED book's own ceiling and its own drawn book, so picking CAP 1 shows
          // CAP 1's commitment against CAP 1's contracts.
          ceiling={data.capacity.totalCommitmentDollars}
          ceilingSublabel={facilityLabel ? `${investorName} · ${facilityLabel}` : investorName}
          outstanding={data.capacity.drawnDollars}
          // "Loans" IS CAP 1 and "Leases" IS CAP 2 — the same column the book selector uses
          // — so on a single facility one of these is the whole drawn book and the OTHER IS
          // NOT IN THE SELECTION. It used to be passed as 0, which rendered a "Leases
          // outstanding 0.0% / $0" card on CAP 1: technically true of the selection, and read
          // by everyone as "the leases book is empty".
          //
          // `undefined` drops the card instead. Note this is the only place a structural zero
          // is known to BE structural — the other two call sites pass figures straight from
          // the API, where a 0 means the book really is empty and the card must stay.
          loans={facility === "CAP1" ? data.capacity.drawnDollars : undefined}
          leases={facility === "CAP2" ? data.capacity.drawnDollars : undefined}
        />
      )}
      {data.capacity.isPlaceholder && (
        <p className="text-[11px] text-muted-foreground">
          {data.capacity.sourceNote ??
            "Ceiling is a placeholder pending confirmation from Finance."}
        </p>
      )}
    </div>
  );
}
